/**
 * Tracking for client.web.admin.
 *
 * ONE sink: Rybbit, the self-hosted product-analytics stream at
 * analytics.carofi.app. Taxonomy names verbatim.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS REPLACED, AND WHY BOTH OLD SINKS WENT
 *
 * This app's tracking has not been recording anything for a long time, in two
 * independent ways:
 *
 *   Amplitude — `amplitude.getInstance().init(...)` was COMMENTED OUT in
 *     initializeTrackingTools while every logEvent call site stayed. amplitude-js
 *     buffers events from an uninitialised instance and never sends them. It was
 *     also 8.x, a version Amplitude has since deprecated.
 *
 *   Google Analytics — `react-ga` is the UNIVERSAL ANALYTICS library, and
 *     Google stopped processing UA data in July 2024. On top of that,
 *     VITE_GA_ID is empty in every .env in this repo, so it was initialising
 *     with an empty measurement id regardless.
 *
 * So there is no migration of live data here — there was no live data. Both
 * dependencies are removed rather than repointed; a dead sink that silently
 * accepts calls is worse than no sink, because it looks instrumented.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT
 *
 *   trackPage / trackSubView / routeTrackingInterceptor — Rybbit derives
 *     pageviews from the URL. The old versions sent `pageview: ${pageId}` and
 *     `modalview: ${subviewId}` as custom events with the id INSIDE the name,
 *     so every route was its own event.
 */

import { TRACKING_RYBBIT_SITE_ID, TRACKING_RYBBIT_HOST } from '@/shared/config/tracking.config';

import type { EventMap, EventName, UserRole } from './events';
import { rybbitIdentify, rybbitReset, rybbitTrack, setGlobalProperties } from './rybbit';

export type { EventName, EventMap } from './events';

/**
 * Staging shares production tracking keys, so without an explicit env property
 * test traffic is indistinguishable from real traffic.
 *
 * Uses Vite's own MODE (set by `--mode` in the build scripts) rather than the
 * repo's VITE_ENV: that variable currently reads `development` in
 * .env.production, so trusting it would label every production event as
 * staging. MODE cannot drift from the build that produced it.
 */
const resolveEnv = (): 'production' | 'staging' =>
  import.meta.env.MODE === 'production' ? 'production' : 'staging';

const resolveLanguage = (): 'en' | 'ar' =>
  typeof document !== 'undefined' && document.documentElement.lang === 'ar' ? 'ar' : 'en';

/**
 * Injects the Rybbit tracker and sets the global properties.
 *
 * The script is added here rather than in index.html because the site id comes
 * from the Vite env, and because no site id must mean NO SCRIPT — an unset id
 * makes tracking a silent no-op instead of writing into the wrong project.
 */
export const initializeTrackingTools = () => {
  try {
    setGlobalProperties({
      app: 'admin_web',
      env: resolveEnv(),
      language: resolveLanguage(),
      user_role: 'anonymous'
    });

    if (!TRACKING_RYBBIT_SITE_ID) return;
    if (document.getElementById('rybbit-script')) return; // idempotent

    const script = document.createElement('script');
    script.id = 'rybbit-script';
    script.src = `${TRACKING_RYBBIT_HOST}/api/script.js`;
    script.defer = true;
    script.setAttribute('data-site-id', TRACKING_RYBBIT_SITE_ID);
    document.head.appendChild(script);
  } catch (error) {
    console.error('Error initializing tracking tools:', error);
  }
};

/**
 * Called on login and whenever the role becomes known.
 *
 * Takes an opaque user id plus, for staff, their display name. The old
 * signature was (userId, email, role) and set the email as a user property on
 * both sinks; an email is exactly what the PII rule in events.ts exists to keep
 * out of analytics, and nothing read it. The name is the one exception — it is
 * what makes a Replay session recognisable without looking the id up, and it
 * goes only to the identify call, never onto event payloads. Role is kept, as a
 * global property, so every event can be split by it without a join.
 */
export const identifyUser = (userId: string, role: UserRole = 'admin', name?: string) => {
  try {
    if (!userId) return;
    rybbitIdentify(userId, { name });
    setGlobalProperties({ user_role: role });
  } catch (error) {
    console.error('Error identifying user:', error);
  }
};

export const resetUser = () => {
  try {
    rybbitReset();
    setGlobalProperties({ user_role: 'anonymous' });
  } catch (error) {
    console.error('Error resetting user:', error);
  }
};

/**
 * The one way to record a product event.
 *
 * `event` is constrained to the taxonomy and `properties` to that event's own
 * shape, so a typo or a missing required property is a build error rather than
 * a new name in the dashboard. The old signature was
 * `trackEvent(action: string, options?: any)`, and the Amplitude call built the
 * name as `${label}|${action}` from two free-text props.
 */
export const trackEvent = <E extends EventName>(event: E, properties: EventMap[E]) => {
  try {
    rybbitTrack(event, properties);
  } catch (error) {
    console.error('Error tracking event:', event, error);
  }
};
